<template>
  <!-- Modelli delle note SOAP (pattern .settings-card): testa con "Nuovo
       modello", la descrizione sotto, griglia di modelli in .settings-pair
       (titolo, tipo, azioni tonde, anteprima delle cinque sezioni), stato vuoto -->
  <section class="shadowedCard p-4 settings-card mb-4">
    <div class="section-head section-head--stack mb-3">
      <b-icon icon="journal-medical" class="section-head__icon" aria-hidden="true"></b-icon>
      <h2 class="section-title mb-0 mr-auto">Modelli delle note cliniche (SOAP)</h2>
      <b-button variant="primary" class="section-head__action" @click="openCreateModal">
        <b-icon icon="plus" aria-hidden="true"></b-icon>
        Nuovo modello
      </b-button>
    </div>
    <p class="settings-card__lead">
      Modelli personali nelle cinque sezioni (Soggettivo, Obiettivo, Valutazione, Piano, Altro) per precompilare note e visite.
    </p>

    <div v-if="loading" class="empty-state">
      <b-spinner label="Caricamento modelli"></b-spinner>
      <p>Caricamento modelli in corso…</p>
    </div>

    <div v-else-if="templates && templates.length > 0" class="settings-pair">
      <div
        v-for="tpl in templates"
        :key="tpl.hashid || tpl.id"
        class="tint-box"
      >
        <div class="section-head mb-2">
          <div class="list-row__body">
            <p class="list-row__title mb-1">{{ tpl.title }}</p>
            <p class="mb-0">
              <b-badge :variant="getTypeBadgeVariant(tpl.type)" pill class="mr-1">{{ getTypeLabel(tpl.type) }}</b-badge>
              <b-badge v-if="tpl.is_default" variant="success" pill>Predefinito</b-badge>
            </p>
          </div>
          <b-button
            variant="white"
            class="btn-icon btn-sm ml-auto"
            @click="openEditModal(tpl)"
            v-b-tooltip.hover
            title="Modifica modello"
            aria-label="Modifica modello"
          >
            <b-icon icon="pencil" aria-hidden="true"></b-icon>
          </b-button>
          <b-button
            variant="white"
            class="btn-icon btn-sm text-danger"
            @click="deleteTemplate(tpl)"
            v-b-tooltip.hover
            title="Elimina modello"
            aria-label="Elimina modello"
          >
            <b-icon icon="trash" aria-hidden="true"></b-icon>
          </b-button>
        </div>
        <div class="list-row__meta">
          <p v-if="tpl.subjective" class="mb-1 text-truncate"><strong>S</strong> {{ tpl.subjective }}</p>
          <p v-if="tpl.objective" class="mb-1 text-truncate"><strong>O</strong> {{ tpl.objective }}</p>
          <p v-if="tpl.assessment" class="mb-1 text-truncate"><strong>A</strong> {{ tpl.assessment }}</p>
          <p v-if="tpl.plan" class="mb-1 text-truncate"><strong>P</strong> {{ tpl.plan }}</p>
          <p v-if="tpl.content" class="mb-0 text-truncate"><strong>Altro</strong> {{ tpl.content }}</p>
        </div>
      </div>
    </div>

    <div v-else class="empty-state">
      <b-icon icon="journal-x" class="empty-state__icon" aria-hidden="true"></b-icon>
      <p>Nessun modello SOAP salvato finora.</p>
      <b-button size="sm" variant="outline-secondary" @click="openCreateModal">
        <b-icon icon="plus-lg" class="mr-1" aria-hidden="true"></b-icon> Crea il primo modello
      </b-button>
    </div>

    <!-- Modale di creazione e modifica -->
    <b-modal
      id="modal-soap-template"
      :title="editingId ? 'Modifica modello SOAP' : 'Nuovo modello SOAP'"
      size="lg"
      hide-footer
      centered
    >
      <b-form @submit.prevent="saveTemplate" @reset="closeModal" class="text-left">
        <b-row>
          <b-col cols="12" md="7" class="mb-3">
            <b-form-group label="Nome del modello *" label-class="filter-bar__label">
              <b-form-input
                type="text"
                v-model="form.title"
                placeholder="Es. Prima visita cardiologica, Controllo generale…"
                required
              ></b-form-input>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="5" class="mb-3">
            <b-form-group label="Tipologia *" label-class="filter-bar__label">
              <b-form-select v-model="form.type" :options="typeOptions" required></b-form-select>
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-checkbox v-model="form.is_default" :value="true" :unchecked-value="false" class="mb-3">
          Imposta come modello predefinito per questa tipologia
        </b-form-checkbox>

        <h3 class="section-eyebrow mb-2">Contenuto delle sezioni SOAP</h3>
        <b-tabs content-class="mt-3">
          <b-tab title="Soggettivo (S)" active>
            <b-form-group label="S - Informazioni riferite dal paziente (sintomi, anamnesi prossima)" label-class="filter-bar__label">
              <b-form-textarea v-model="form.subjective" rows="4" placeholder="Testo precompilato per la sezione Soggettivo…"></b-form-textarea>
            </b-form-group>
          </b-tab>
          <b-tab title="Obiettivo (O)">
            <b-form-group label="O - Reperti dell'esame obiettivo, parametri vitali e misurazioni" label-class="filter-bar__label">
              <b-form-textarea v-model="form.objective" rows="4" placeholder="Testo precompilato per la sezione Obiettivo…"></b-form-textarea>
            </b-form-group>
          </b-tab>
          <b-tab title="Valutazione (A)">
            <b-form-group label="A - Diagnosi, diagnosi differenziale o valutazione clinica" label-class="filter-bar__label">
              <b-form-textarea v-model="form.assessment" rows="4" placeholder="Testo precompilato per la sezione Valutazione…"></b-form-textarea>
            </b-form-group>
          </b-tab>
          <b-tab title="Piano (P)">
            <b-form-group label="P - Terapia, esami prescritti, controlli e follow-up" label-class="filter-bar__label">
              <b-form-textarea v-model="form.plan" rows="4" placeholder="Testo precompilato per la sezione Piano…"></b-form-textarea>
            </b-form-group>
          </b-tab>
          <b-tab title="Altro">
            <b-form-group label="Altro - Annotazioni cliniche libere o indicazioni aggiuntive" label-class="filter-bar__label">
              <b-form-textarea v-model="form.content" rows="4" placeholder="Testo precompilato per altre note…"></b-form-textarea>
            </b-form-group>
          </b-tab>
        </b-tabs>

        <p class="list-row__meta mt-2 mb-0">* Campi obbligatori</p>

        <div class="filter-bar__actions justify-content-end pt-3 border-top mt-3">
          <b-button type="reset" variant="outline-secondary">Annulla</b-button>
          <b-button type="submit" variant="secondary" :disabled="saving">
            <b-spinner small v-if="saving" class="mr-1" label="Salvataggio"></b-spinner>
            {{ editingId ? 'Aggiorna modello' : 'Salva modello' }}
          </b-button>
        </div>
      </b-form>
    </b-modal>

    <b-modal
      id="modal-elimina-modello-soap"
      ref="modal-elimina-modello-soap"
      :title="$t('SoapTemplatesManager.conferma_eliminazione_titolo')"
      size="sm"
      :ok-title="$t('SoapTemplatesManager.elimina')"
      :cancel-title="$t('annulla')"
      ok-variant="popup-button"
      cancel-variant="popup-button mr-2"
      footer-border-variant="white"
      @ok="confermaEliminazione"
      @cancel="$bvModal.hide('modal-elimina-modello-soap')"
      @hidden="templateDaEliminare = null"
      centered
    >
      <div class="px-4 py-3">
        {{
          $t("SoapTemplatesManager.conferma_eliminazione", {
            titolo: templateDaEliminare ? templateDaEliminare.title : "",
          })
        }}
      </div>
    </b-modal>
  </section>
</template>

<script>
import { mapActions } from "vuex";
import { soapTemplateService } from "@/_services";

export default {
  name: "SoapTemplatesManager",
  data() {
    return {
      templates: [],
      loading: false,
      saving: false,
      editingId: null,
      templateDaEliminare: null,
      form: {
        title: "",
        type: "soapnote",
        subjective: "",
        objective: "",
        assessment: "",
        plan: "",
        content: "",
        is_default: false,
      },
      typeOptions: [
        { value: "soapnote", text: "Nota Clinica / SOAP" },
        { value: "visit", text: "Visita / Prestazione" },
        { value: "prescription", text: "Prescrizione" },
        { value: "diagnosis", text: "Diagnosi" },
      ],
    };
  },
  methods: {
    ...mapActions({
      errorAlert: "alert/error",
      successAlert: "alert/success",
    }),
    fetchTemplates() {
      this.loading = true;
      soapTemplateService
        .getAll()
        .then((res) => {
          this.templates = res.data && res.data.data ? res.data.data : [];
          this.loading = false;
        })
        .catch(() => {
          this.templates = [];
          this.loading = false;
          this.errorAlert("Impossibile caricare i modelli SOAP");
        });
    },
    openCreateModal() {
      this.editingId = null;
      this.form = {
        title: "",
        type: "soapnote",
        subjective: "",
        objective: "",
        assessment: "",
        plan: "",
        content: "",
        is_default: false,
      };
      this.$bvModal.show("modal-soap-template");
    },
    openEditModal(tpl) {
      this.editingId = tpl.hashid || tpl.id;
      this.form = {
        title: tpl.title || "",
        type: tpl.type || "soapnote",
        subjective: tpl.subjective || "",
        objective: tpl.objective || "",
        assessment: tpl.assessment || "",
        plan: tpl.plan || "",
        content: tpl.content || "",
        is_default: !!tpl.is_default,
      };
      this.$bvModal.show("modal-soap-template");
    },
    closeModal() {
      this.$bvModal.hide("modal-soap-template");
    },
    saveTemplate() {
      this.saving = true;
      const action = this.editingId
        ? soapTemplateService.update(this.editingId, this.form)
        : soapTemplateService.create(this.form);

      action
        .then(() => {
          this.saving = false;
          this.closeModal();
          this.successAlert(
            this.editingId
              ? "Modello aggiornato con successo"
              : "Modello salvato con successo"
          );
          this.fetchTemplates();
        })
        .catch(() => {
          this.saving = false;
          this.errorAlert("Errore durante il salvataggio del modello SOAP");
        });
    },
    deleteTemplate(tpl) {
      this.templateDaEliminare = tpl;
      this.$bvModal.show("modal-elimina-modello-soap");
    },
    confermaEliminazione() {
      const tpl = this.templateDaEliminare;
      if (!tpl) {
        return;
      }
      const id = tpl.hashid || tpl.id;
      soapTemplateService
        .remove(id)
        .then(() => {
          this.successAlert(this.$t("SoapTemplatesManager.eliminato"));
          this.fetchTemplates();
        })
        .catch(() => {
          this.errorAlert(this.$t("SoapTemplatesManager.eliminazione_fallita"));
        });
    },
    getTypeLabel(type) {
      const found = this.typeOptions.find((o) => o.value === type);
      return found ? found.text : type;
    },
    getTypeBadgeVariant(type) {
      switch (type) {
        case "visit":
          return "info";
        case "prescription":
          return "warning";
        case "diagnosis":
          return "dark";
        default:
          return "secondary";
      }
    },
  },
  mounted() {
    this.fetchTemplates();
  },
};
</script>
